import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { AZURE_LOCAL_MARKET_MAPPINGS } from "../constant/access-request";
import { VCloudSmartApiService } from "./v-cloud-smart-api.service";
import {
  buildAdminNotificationsEndpoint,
  buildUserNotificationsEndpoint,
  createAdminPage,
  extractCreatedNotificationIds,
  extractNotificationList,
  extractSingleNotification,
  getActiveNotifications,
  getExpirationStatus,
  getNotificationsForAudience,
  mapDraftToUpdatePayload,
  mapDraftToPayload,
  normalizeNotification,
} from "./notifications/notifications.helpers";
import {
  AdminNotificationsPage,
  AdminNotificationsQuery,
  AdminNotificationsResponse,
  CreateNotificationPayload,
  CreateNotificationResponse,
  DeleteNotificationResponse,
  NotificationApiRecord,
  NotificationDraft,
  NotificationItem,
  NotificationMarkPayload,
  NotificationMarkResponse,
  NotificationMarkAction,
  UpdateNotificationPayload,
  UserNotificationsQuery,
  UserNotificationsResponse,
} from "./notifications/notifications.types";
import { VcloudHttpClientService } from "./vcloud-http-client.service";

export {
  AdminNotificationsPage,
  AdminNotificationsQuery,
  NotificationActionParams,
  NotificationDraft,
  NotificationItem,
  NotificationPriority,
  NotificationSeverity,
  NotificationType,
  UserNotificationsQuery,
} from "./notifications/notifications.types";

@Injectable({
  providedIn: "root",
})
export class NotificationsService {
  private static readonly DEFAULT_ADMIN_LIMIT: number = 50;
  private readonly storageKey: string = "pcs.notifications";
  private readonly userStorageKey: string = "pcs.user.notifications";
  private readonly userNotificationsLastSyncKey: string = "pcs.user.notifications.lastSync";
  private readonly adminNotificationsEndpoint: string = "notifications/admin";
  private readonly createNotificationEndpoint: string = "notifications";
  private readonly userNotificationsEndpoint: string = "notifications/user";
  private readonly markNotificationEndpoint: string = "notifications/mark";
  private readonly vCloudApi: VCloudSmartApiService = inject(VCloudSmartApiService);
  private readonly httpClient: VcloudHttpClientService = inject(VcloudHttpClientService);
  private readonly notificationsSubject: BehaviorSubject<NotificationItem[]> = new BehaviorSubject<NotificationItem[]>(
    this.loadStoredNotifications(this.storageKey),
  );
  private readonly userNotificationsSubject: BehaviorSubject<NotificationItem[]> = new BehaviorSubject<
    NotificationItem[]
  >(this.loadStoredNotifications(this.userStorageKey));

  public get notifications(): NotificationItem[] {
    return getActiveNotifications(this.notificationsSubject.value);
  }

  public getNotificationsForAudience(
    currentTenant?: string | null,
    currentPagePath?: string | null,
    currentUserEmail?: string | null,
  ): NotificationItem[] {
    return getNotificationsForAudience(
      getActiveNotifications(this.userNotificationsSubject.value),
      currentTenant,
      AZURE_LOCAL_MARKET_MAPPINGS,
      currentPagePath,
      currentUserEmail,
    );
  }

  public getUnreadCountForAudience(
    currentTenant?: string | null,
    currentPagePath?: string | null,
    currentUserEmail?: string | null,
  ): number {
    return this.getNotificationsForAudience(currentTenant, currentPagePath, currentUserEmail).filter(
      (notification) => notification.unread,
    ).length;
  }

  public async addNotification(draft: NotificationDraft): Promise<NotificationItem | null> {
    const previousNotifications: NotificationItem[] = this.notifications;
    const notification: NotificationItem = normalizeNotification(undefined, draft);
    this.updateNotifications([notification, ...previousNotifications.filter((item) => item.id !== notification.id)]);

    try {
      const payload: CreateNotificationPayload = mapDraftToPayload(draft);

      const response: NotificationItem | { notification?: NotificationItem } | CreateNotificationResponse =
        await firstValueFrom(
          this.httpClient.post<
            NotificationItem | { notification?: NotificationItem } | CreateNotificationResponse,
            CreateNotificationPayload
          >(this.createNotificationEndpoint, payload),
        );
      const responseNotification: NotificationItem | undefined = extractSingleNotification(response);
      const createdNotificationIds: string[] = extractCreatedNotificationIds(response);

      if (responseNotification?.id) {
        const resolvedNotification: NotificationItem = normalizeNotification(responseNotification, draft);
        this.updateNotifications([
          resolvedNotification,
          ...this.notifications.filter((item) => item.id !== notification.id),
        ]);
        return resolvedNotification;
      }

      const refreshedNotifications: NotificationItem[] = await this.refreshNotificationsFromAdminApi({
        limit: NotificationsService.DEFAULT_ADMIN_LIMIT,
        offset: 0,
      });

      if (createdNotificationIds.length > 0) {
        const createdNotification: NotificationItem | undefined = refreshedNotifications.find((item) =>
          createdNotificationIds.includes(item.id),
        );
        if (createdNotification) {
          return createdNotification;
        }
      }

      return refreshedNotifications[0] ?? notification;
    } catch {
      this.updateNotifications(previousNotifications);
      return null;
    }
  }

  public async updateNotification(
    notificationId: string,
    draft: NotificationDraft,
    tenant?: string,
  ): Promise<NotificationItem | null> {
    const payload: UpdateNotificationPayload = mapDraftToUpdatePayload(draft);

    try {
      const endpoint: string = this.buildNotificationEndpoint(notificationId, tenant);
      const response: NotificationItem | { notification?: NotificationItem } | { message?: string } =
        await firstValueFrom(
          this.httpClient.patch<
            NotificationItem | { notification?: NotificationItem } | { message?: string },
            UpdateNotificationPayload
          >(endpoint, payload),
        );

      const responseNotification: NotificationItem | undefined = extractSingleNotification(response);
      if (responseNotification?.id) {
        const resolvedNotification: NotificationItem = normalizeNotification(responseNotification, draft);
        this.updateNotifications(
          this.notifications.map((item) => (item.id === notificationId ? resolvedNotification : item)),
        );
        return resolvedNotification;
      }

      const refreshedNotifications: NotificationItem[] = await this.refreshNotificationsFromAdminApi({
        limit: NotificationsService.DEFAULT_ADMIN_LIMIT,
        offset: 0,
      });
      return refreshedNotifications.find((item) => item.id === notificationId) ?? null;
    } catch {
      return null;
    }
  }

  public async refreshNotificationsFromAdminApi(query: AdminNotificationsQuery = {}): Promise<NotificationItem[]> {
    try {
      const page: AdminNotificationsPage = await this.getAdminNotificationsPage(query);
      this.updateNotifications(page.notifications);
      return this.notifications;
    } catch {
      return this.notifications;
    }
  }

  public async getAdminNotificationsPage(query: AdminNotificationsQuery = {}): Promise<AdminNotificationsPage> {
    const requestedLimit: number = query.limit ?? NotificationsService.DEFAULT_ADMIN_LIMIT;
    const requestedOffset: number = query.offset ?? 0;

    try {
      const endpoint: string = buildAdminNotificationsEndpoint(query, this.adminNotificationsEndpoint);
      const response: AdminNotificationsResponse | NotificationApiRecord[] = await firstValueFrom(
        this.httpClient.get<AdminNotificationsResponse | NotificationApiRecord[]>(endpoint),
      );
      const remoteNotifications: NotificationItem[] = extractNotificationList(response);

      return createAdminPage(remoteNotifications, response, requestedLimit, requestedOffset);
    } catch {
      const cachedNotifications: NotificationItem[] = this.notifications;
      const notifications: NotificationItem[] = cachedNotifications.slice(
        requestedOffset,
        requestedOffset + requestedLimit,
      );
      return createAdminPage(notifications, { total: cachedNotifications.length }, requestedLimit, requestedOffset);
    }
  }

  public async refreshNotificationsFromUserApi(query: UserNotificationsQuery = {}): Promise<NotificationItem[]> {
    const syncStartedAt: number = Date.now();

    try {
      const endpoint: string = buildUserNotificationsEndpoint(query, this.userNotificationsEndpoint);
      const response: UserNotificationsResponse = await firstValueFrom(
        this.httpClient.get<UserNotificationsResponse>(endpoint),
      );
      const remoteNotifications: NotificationItem[] = extractNotificationList(response);
      const reconciledNotifications: NotificationItem[] = this.reconcileUserNotifications(remoteNotifications);

      this.updateUserNotifications(reconciledNotifications);
      localStorage.setItem(this.userNotificationsLastSyncKey, String(syncStartedAt));
      return getActiveNotifications(this.userNotificationsSubject.value);
    } catch {
      return getActiveNotifications(this.userNotificationsSubject.value);
    }
  }

  public async deleteNotification(notificationId: string, tenant?: string): Promise<boolean> {
    const previousNotifications: NotificationItem[] = this.notifications;
    this.updateNotifications(previousNotifications.filter((notification) => notification.id !== notificationId));

    try {
      const endpoint: string = this.buildNotificationEndpoint(notificationId, tenant);
      await firstValueFrom(this.httpClient.delete<DeleteNotificationResponse>(endpoint));
      return true;
    } catch {
      this.updateNotifications(previousNotifications);
      return false;
    }
  }

  public async markAsRead(notificationId: string): Promise<void> {
    await this.markNotification(notificationId, "read");
  }

  public async markNotification(notificationId: string, action: NotificationMarkAction): Promise<boolean> {
    const previousNotifications: NotificationItem[] = this.userNotificationsSubject.value;
    const shouldUpdateUnread: boolean = action === "read" || action === "unread";

    if (shouldUpdateUnread) {
      const unreadValue: boolean = action === "unread";
      const updated: NotificationItem[] = previousNotifications.map((notification) =>
        notification.id === notificationId ? { ...notification, unread: unreadValue } : notification,
      );
      this.updateUserNotifications(updated);
    }

    try {
      const payload: NotificationMarkPayload = {
        notification_id: notificationId,
        action,
      };

      await firstValueFrom(
        this.httpClient.post<NotificationMarkResponse, NotificationMarkPayload>(this.markNotificationEndpoint, payload),
      );
      return true;
    } catch {
      if (shouldUpdateUnread) {
        this.updateUserNotifications(previousNotifications);
      }

      return false;
    }
  }

  public getTypeLabel(type: string): string {
    return type.split("_").join(" ");
  }

  public getExpirationStatus(notification: NotificationItem): string {
    return getExpirationStatus(notification);
  }

  private updateNotifications(notifications: NotificationItem[]): void {
    this.persistNotifications(this.storageKey, this.notificationsSubject, notifications);
  }

  private updateUserNotifications(notifications: NotificationItem[]): void {
    this.persistNotifications(this.userStorageKey, this.userNotificationsSubject, notifications);
  }

  private persistNotifications(
    storageKey: string,
    subject: BehaviorSubject<NotificationItem[]>,
    notifications: NotificationItem[],
  ): void {
    const activeNotifications: NotificationItem[] = getActiveNotifications(notifications);
    localStorage.setItem(storageKey, JSON.stringify(activeNotifications));
    subject.next(activeNotifications);
  }

  private loadStoredNotifications(storageKey: string): NotificationItem[] {
    const saved: string | null = localStorage.getItem(storageKey);

    if (!saved) {
      return [];
    }

    try {
      const parsed: NotificationItem[] = JSON.parse(saved) as NotificationItem[];
      return getActiveNotifications(parsed);
    } catch {
      localStorage.removeItem(storageKey);
      return [];
    }
  }

  private reconcileUserNotifications(remoteNotifications: NotificationItem[]): NotificationItem[] {
    const previousNotifications: NotificationItem[] = this.userNotificationsSubject.value;
    const previousById: Map<string, NotificationItem> = new Map(
      previousNotifications.map((notification) => [notification.id, notification]),
    );
    const lastSyncRaw: string | null = localStorage.getItem(this.userNotificationsLastSyncKey);
    const lastSyncAt: number = Number(lastSyncRaw || "0");

    return remoteNotifications.map((notification) => {
      const previous: NotificationItem | undefined = previousById.get(notification.id);

      if (previous) {
        if (previous.unread && !notification.unread) {
          return { ...notification, unread: true };
        }
        return notification;
      }

      const notificationTime: number = new Date(notification.timestamp).getTime();
      const isNewSinceLastSync: boolean =
        Number.isFinite(notificationTime) && lastSyncAt > 0 && notificationTime > lastSyncAt;

      if (!notification.unread && isNewSinceLastSync) {
        return { ...notification, unread: true };
      }

      return notification;
    });
  }

  private buildNotificationEndpoint(notificationId: string, tenant?: string): string {
    const endpointBase: string = `${this.createNotificationEndpoint}/${encodeURIComponent(notificationId)}`;

    if (!tenant) {
      return endpointBase;
    }

    const params: URLSearchParams = new URLSearchParams({ tenant });
    return `${endpointBase}?${params.toString()}`;
  }
}
